import React, { useState, useEffect } from 'react';
import { 
    TrendingUp, TrendingDown, AlertTriangle, Users, 
    Plus, Mic, ArrowUpRight, ArrowDownRight, RefreshCw,
    BarChart3, ArrowUp, ArrowDown, Bell, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, Area, AreaChart } from 'recharts';
import MobileLayout from '../components/layout/MobileLayout';
import VoiceEntryModal from '../components/voice/VoiceEntryModal';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotifications } from '../contexts/NotificationContext';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
    const { authAxios, user } = useAuth();
    const { t, language } = useLanguage();
    const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
    const navigate = useNavigate();
    
    const [stats, setStats] = useState(null);
    const [weeklyStats, setWeeklyStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [voiceOpen, setVoiceOpen] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);

    const fetchDashboardStats = async () => {
        setLoading(true);
        try {
            const [dashboardRes, weeklyRes] = await Promise.all([
                authAxios.get('/reports/dashboard'),
                authAxios.get('/reports/weekly-comparison')
            ]);
            setStats(dashboardRes.data);
            setWeeklyStats(weeklyRes.data);
        } catch (err) {
            console.error('Dashboard fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardStats();
    }, []);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatCompactCurrency = (amount) => {
        if (Math.abs(amount) >= 100000) {
            return `₹${(amount / 100000).toFixed(1)}L`;
        } else if (Math.abs(amount) >= 1000) {
            return `₹${(amount / 1000).toFixed(1)}K`;
        }
        return `₹${amount}`;
    };

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString(language === 'hi' ? 'hi-IN' : 'en-IN', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderChangeIndicator = (change, isPositiveGood = true) => {
        const isPositive = change > 0;
        const isGood = isPositiveGood ? isPositive : !isPositive;
        
        return (
            <span className={cn(
                "flex items-center text-sm font-semibold",
                isGood ? "text-emerald-600" : "text-rose-600"
            )}>
                {isPositive ? (
                    <ArrowUp className="w-4 h-4" />
                ) : (
                    <ArrowDown className="w-4 h-4" />
                )}
                {Math.abs(change)}%
            </span>
        );
    };

    if (loading) {
        return (
            <MobileLayout>
                <div className="space-y-4">
                    <Skeleton className="h-8 w-48" />
                    <div className="bento-grid">
                        <Skeleton className="h-28" />
                        <Skeleton className="h-28" />
                        <Skeleton className="h-28 col-span-2" />
                    </div>
                    <Skeleton className="h-48" />
                    <Skeleton className="h-64" />
                </div>
            </MobileLayout>
        );
    }

    return (
        <MobileLayout>
            <div className="space-y-6" data-testid="dashboard">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold font-heading">
                            {language === 'en' ? 'Hello' : 'नमस्ते'}, {user?.owner_name || user?.business_name || 'Merchant'}!
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            {new Date().toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long'
                            })}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Notification Bell */}
                        <div className="relative">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setShowNotifications(!showNotifications)}
                                data-testid="notification-bell"
                                className="relative"
                            >
                                <Bell className="h-5 w-5" />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                                        {unreadCount > 9 ? '9+' : unreadCount}
                                    </span>
                                )}
                            </Button>
                            
                            {/* Notification Dropdown */}
                            {showNotifications && (
                                <div className="absolute right-0 top-12 w-80 bg-background border rounded-lg shadow-lg z-50 max-h-96 overflow-auto">
                                    <div className="p-3 border-b flex items-center justify-between">
                                        <span className="font-semibold">
                                            {language === 'en' ? 'Notifications' : 'सूचनाएं'}
                                        </span>
                                        {notifications.length > 0 && (
                                            <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                                                {language === 'en' ? 'Mark all read' : 'सब पढ़े'}
                                            </Button>
                                        )}
                                    </div>
                                    <div className="divide-y">
                                        {notifications.length === 0 ? (
                                            <div className="p-4 text-center text-muted-foreground">
                                                {language === 'en' ? 'No notifications' : 'कोई सूचना नहीं'}
                                            </div>
                                        ) : (
                                            notifications.slice(0, 10).map((notif) => (
                                                <div
                                                    key={notif.id}
                                                    className={cn(
                                                        "p-3 cursor-pointer hover:bg-muted/50 transition-colors",
                                                        !notif.read && "bg-primary/5"
                                                    )}
                                                    onClick={() => {
                                                        markAsRead(notif.id);
                                                        if (notif.type === 'high_risk') {
                                                            navigate('/transactions');
                                                        }
                                                        setShowNotifications(false);
                                                    }}
                                                >
                                                    <div className="flex items-start gap-2">
                                                        <AlertTriangle className={cn(
                                                            "w-5 h-5 mt-0.5",
                                                            notif.type === 'high_risk' ? "text-red-500" : "text-yellow-500"
                                                        )} />
                                                        <div className="flex-1">
                                                            <p className="font-medium text-sm">{notif.title}</p>
                                                            <p className="text-xs text-muted-foreground">{notif.message}</p>
                                                        </div>
                                                        {!notif.read && (
                                                            <span className="w-2 h-2 bg-primary rounded-full" />
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={fetchDashboardStats}
                            data-testid="refresh-btn"
                        >
                            <RefreshCw className="h-5 w-5" />
                        </Button>
                    </div>
                </div>

                {/* Stats Bento Grid */}
                <div className="bento-grid">
                    {/* Today's Sales */}
                    <Card className="bento-card bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800" data-testid="today-sales-card">
                        <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium uppercase">{t('todaySales')}</p>
                                    <p className="text-2xl font-bold font-number text-emerald-700 dark:text-emerald-300 mt-1">
                                        {formatCurrency(stats?.today?.sales || 0)}
                                    </p>
                                </div>
                                <div className="p-2 rounded-full bg-emerald-100 dark:bg-emerald-800/50">
                                    <TrendingUp className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Today's Expenses */}
                    <Card className="bento-card bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800" data-testid="today-expenses-card">
                        <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs text-rose-600 dark:text-rose-400 font-medium uppercase">{t('todayExpenses')}</p>
                                    <p className="text-2xl font-bold font-number text-rose-700 dark:text-rose-300 mt-1">
                                        {formatCurrency(stats?.today?.expenses || 0)}
                                    </p>
                                </div>
                                <div className="p-2 rounded-full bg-rose-100 dark:bg-rose-800/50">
                                    <TrendingDown className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Today's Profit */}
                    <Card className={cn(
                        "bento-card bento-card-full",
                        (stats?.today?.profit || 0) >= 0 
                            ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                            : "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
                    )} data-testid="today-profit-card">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-medium uppercase text-muted-foreground">{t('todayProfit')}</p>
                                    <p className={cn(
                                        "text-3xl font-bold font-number mt-1",
                                        (stats?.today?.profit || 0) >= 0 
                                            ? "text-blue-700 dark:text-blue-300"
                                            : "text-amber-700 dark:text-amber-300"
                                    )}>
                                        {formatCurrency(stats?.today?.profit || 0)}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">
                                        {stats?.today?.transaction_count || 0} {language === 'en' ? 'transactions' : 'लेन-देन'}
                                    </span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-3">
                    <Button
                        className="flex-1 h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600"
                        onClick={() => navigate('/transactions?add=sale')}
                        data-testid="quick-add-sale"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        {t('addSale')}
                    </Button>
                    <Button
                        className="flex-1 h-12 rounded-xl bg-rose-500 hover:bg-rose-600"
                        onClick={() => navigate('/transactions?add=expense')}
                        data-testid="quick-add-expense"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        {t('addExpense')}
                    </Button>
                </div>

                {/* Weekly Stats Widget */}
                {weeklyStats && (
                    <Card className="overflow-hidden" data-testid="weekly-stats-widget">
                        <CardHeader className="pb-2 bg-gradient-to-r from-primary/10 to-transparent">
                            <CardTitle className="text-base flex items-center gap-2">
                                <BarChart3 className="w-5 h-5 text-primary" />
                                {language === 'en' ? 'This Week vs Last Week' : 'इस हफ्ते vs पिछले हफ्ते'}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            {/* Stats Comparison */}
                            <div className="grid grid-cols-3 gap-3 mb-4">
                                <div className="text-center p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                                    <p className="text-xs text-muted-foreground uppercase">
                                        {language === 'en' ? 'Sales' : 'बिक्री'}
                                    </p>
                                    <p className="font-bold font-number text-emerald-600 text-lg">
                                        {formatCompactCurrency(weeklyStats.this_week.sales)}
                                    </p>
                                    {renderChangeIndicator(weeklyStats.changes.sales, true)}
                                </div>
                                <div className="text-center p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20">
                                    <p className="text-xs text-muted-foreground uppercase">
                                        {language === 'en' ? 'Expenses' : 'खर्च'}
                                    </p>
                                    <p className="font-bold font-number text-rose-600 text-lg">
                                        {formatCompactCurrency(weeklyStats.this_week.expenses)}
                                    </p>
                                    {renderChangeIndicator(weeklyStats.changes.expenses, false)}
                                </div>
                                <div className={cn(
                                    "text-center p-3 rounded-lg",
                                    weeklyStats.this_week.profit >= 0 
                                        ? "bg-blue-50 dark:bg-blue-900/20" 
                                        : "bg-amber-50 dark:bg-amber-900/20"
                                )}>
                                    <p className="text-xs text-muted-foreground uppercase">
                                        {language === 'en' ? 'Profit' : 'मुनाफा'}
                                    </p>
                                    <p className={cn(
                                        "font-bold font-number text-lg",
                                        weeklyStats.this_week.profit >= 0 ? "text-blue-600" : "text-amber-600"
                                    )}>
                                        {weeklyStats.this_week.profit >= 0 ? '+' : ''}{formatCompactCurrency(weeklyStats.this_week.profit)}
                                    </p>
                                    {renderChangeIndicator(weeklyStats.changes.profit, true)}
                                </div>
                            </div>

                            {/* Weekly Chart */}
                            <div className="h-40">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={weeklyStats.daily_data}>
                                        <defs>
                                            <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                                                <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                                            </linearGradient>
                                            <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#2563EB" stopOpacity={0.3}/>
                                                <stop offset="95%" stopColor="#2563EB" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <XAxis 
                                            dataKey="day" 
                                            tick={{ fontSize: 10 }} 
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis hide />
                                        <Tooltip 
                                            formatter={(value) => formatCurrency(value)}
                                            contentStyle={{ 
                                                background: 'hsl(var(--background))',
                                                border: '1px solid hsl(var(--border))',
                                                borderRadius: '8px',
                                                fontSize: '12px'
                                            }}
                                        />
                                        <Area 
                                            type="monotone" 
                                            dataKey="sales" 
                                            stroke="#10B981" 
                                            strokeWidth={2}
                                            fillOpacity={1} 
                                            fill="url(#salesGradient)"
                                            name={language === 'en' ? 'Sales' : 'बिक्री'}
                                        />
                                        <Area 
                                            type="monotone" 
                                            dataKey="profit" 
                                            stroke="#2563EB" 
                                            strokeWidth={2}
                                            fillOpacity={1} 
                                            fill="url(#profitGradient)"
                                            name={language === 'en' ? 'Profit' : 'मुनाफा'}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Summary Text */}
                            <p className="text-sm text-center text-muted-foreground mt-2">
                                {weeklyStats.changes.profit > 0 ? (
                                    language === 'en' 
                                        ? `🎉 Profit up ${weeklyStats.changes.profit}% vs last week!`
                                        : `🎉 पिछले हफ्ते से मुनाफा ${weeklyStats.changes.profit}% बढ़ा!`
                                ) : weeklyStats.changes.profit < 0 ? (
                                    language === 'en'
                                        ? `📉 Profit down ${Math.abs(weeklyStats.changes.profit)}% vs last week`
                                        : `📉 पिछले हफ्ते से मुनाफा ${Math.abs(weeklyStats.changes.profit)}% घटा`
                                ) : (
                                    language === 'en' ? 'Profit same as last week' : 'मुनाफा पिछले हफ्ते जैसा'
                                )}
                            </p>
                        </CardContent>
                    </Card>
                )}

                {/* Alerts Section */}
                {(stats?.alerts?.high_risk_count > 0 || stats?.alerts?.pending_due > 0) && (
                    <div className="space-y-3">
                        {stats?.alerts?.high_risk_count > 0 && (
                            <Card className="border-l-4 border-l-rose-500 bg-rose-50 dark:bg-rose-900/20" data-testid="risk-alert-card">
                                <CardContent className="p-4 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <AlertTriangle className="w-5 h-5 text-rose-500" />
                                        <div>
                                            <p className="font-medium text-rose-700 dark:text-rose-300">
                                                {stats.alerts.high_risk_count} {t('highRiskAlerts')}
                                            </p>
                                            <p className="text-xs text-rose-600 dark:text-rose-400">
                                                {language === 'en' ? 'Requires your attention' : 'आपके ध्यान की आवश्यकता है'}
                                            </p>
                                        </div>
                                    </div>
                                    <Button 
                                        size="sm" 
                                        variant="destructive"
                                        onClick={() => navigate('/transactions?filter=high_risk')}
                                    >
                                        {t('viewAll')}
                                    </Button>
                                </CardContent>
                            </Card>
                        )}

                        {stats?.alerts?.pending_due > 0 && (
                            <Card className="border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-900/20" data-testid="due-alert-card">
                                <CardContent className="p-4 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <Users className="w-5 h-5 text-amber-500" />
                                        <div>
                                            <p className="font-medium text-amber-700 dark:text-amber-300">
                                                {formatCurrency(stats.alerts.pending_due)} {t('pendingDues')}
                                            </p>
                                            <p className="text-xs text-amber-600 dark:text-amber-400">
                                                {stats.alerts.overdue_count || 0} {language === 'en' ? 'overdue' : 'अतिदेय'}
                                            </p>
                                        </div>
                                    </div>
                                    <Button 
                                        size="sm" 
                                        variant="outline"
                                        onClick={() => navigate('/khata')}
                                    >
                                        {t('viewAll')}
                                    </Button>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                )}

                {/* Recent Transactions */}
                <Card data-testid="recent-transactions-card">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg">{t('recentTransactions')}</CardTitle>
                            <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => navigate('/transactions')}
                            >
                                {t('viewAll')}
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {stats?.recent_transactions?.length > 0 ? (
                            stats.recent_transactions.map((tx) => (
                                <div 
                                    key={tx.id}
                                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                                    onClick={() => navigate(`/transactions/${tx.id}`)}
                                    data-testid={`transaction-${tx.id}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={cn(
                                            "p-2 rounded-full",
                                            tx.type === 'sale' 
                                                ? "bg-emerald-100 dark:bg-emerald-900/30"
                                                : "bg-rose-100 dark:bg-rose-900/30"
                                        )}>
                                            {tx.type === 'sale' ? (
                                                <ArrowUpRight className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                            ) : (
                                                <ArrowDownRight className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-sm">{tx.category}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {formatTime(tx.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className={cn(
                                            "font-bold font-number",
                                            tx.type === 'sale' ? "text-emerald-600" : "text-rose-600"
                                        )}>
                                            {tx.type === 'sale' ? '+' : '-'}{formatCurrency(tx.amount)}
                                        </p>
                                        {tx.risk_level && (
                                            <Badge 
                                                variant="outline" 
                                                className={cn(
                                                    "text-xs",
                                                    tx.risk_level === 'high' && "border-rose-500 text-rose-500",
                                                    tx.risk_level === 'medium' && "border-amber-500 text-amber-500",
                                                    tx.risk_level === 'low' && "border-emerald-500 text-emerald-500"
                                                )}
                                            >
                                                {tx.risk_score}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                <p>{language === 'en' ? 'No transactions yet' : 'अभी तक कोई लेन-देन नहीं'}</p>
                                <p className="text-sm">{language === 'en' ? 'Add your first sale or expense' : 'अपनी पहली बिक्री या खर्च जोड़ें'}</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Subscription Info */}
                {stats?.subscription?.plan === 'free' && (
                    <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20" data-testid="subscription-banner">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium">{t('freePlan')}</p>
                                    <p className="text-sm text-muted-foreground">
                                        {stats.subscription.transactions_used}/{stats.subscription.limit} {language === 'en' ? 'transactions used' : 'लेन-देन उपयोग हुए'}
                                    </p>
                                </div>
                                <Button 
                                    size="sm"
                                    onClick={() => navigate('/settings?tab=subscription')}
                                >
                                    {t('upgrade')}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Voice FAB */}
                <button
                    className="fab-voice"
                    onClick={() => setVoiceOpen(true)}
                    data-testid="voice-fab"
                    aria-label={t('voiceEntry')}
                >
                    <Mic className="w-6 h-6" />
                </button>

                {/* Voice Modal */}
                <VoiceEntryModal
                    open={voiceOpen}
                    onClose={() => setVoiceOpen(false)}
                    onSuccess={() => {
                        fetchDashboardStats();
                    }}
                />

                {/* Disclaimer */}
                <div className="disclaimer-banner text-center">
                    {t('disclaimer')}
                </div>
            </div>
        </MobileLayout>
    );
};

export default Dashboard;
